import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Eye,
  User,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { FewShotExample } from '@/types/fewShot';
import { ExampleEditForm } from './ExampleEditForm';
import { getMachineLabel, getMachineLabels } from '@/constants/machines';

interface ExampleRowProps {
  example: FewShotExample;
  onDelete?: (example: FewShotExample) => void;
}

export function ExampleRow({ example, onDelete }: ExampleRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { isAdmin } = useAuth();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  return (
    <div className={`border rounded-lg transition-all hover:shadow-sm ${
      example.is_good_example ? 'border-green-200' : 'border-red-200'
    }`}>
      {/* Compact list row */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => !isEditing && setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Expand/Collapse icon */}
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-auto"
              onClick={(e) => {
                e.stopPropagation();
                if (!isEditing) setExpanded(!expanded);
              }}
            >
              {expanded ?
                <ChevronDown className="h-4 w-4" /> :
                <ChevronRight className="h-4 w-4" />
              }
            </Button>

            {/* Status indicator */}
            <div className="flex items-center space-x-2">
              {example.is_good_example ? (
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              )}
            </div>

            {/* Question preview */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium line-clamp-1">{example.question}</p>
              <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-1">
                {example.machines && example.machines.length > 0 && (
                  <>
                    {example.machines.map((code) => (
                      <Badge key={code} variant="outline" className="text-xs px-1 py-0">
                        {getMachineLabel(code)}
                      </Badge>
                    ))}
                  </>
                )}
                {example.error_code && (
                  <Badge variant="outline" className="text-xs px-1 py-0">
                    {example.error_code}
                  </Badge>
                )}
                <span>•</span>
                <span>{formatDate(example.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            {!isEditing && isAdmin && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setExpanded(true);
                    setIsEditing(true);
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete?.(example)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expanded detail view */}
      {expanded && (
        <div className="border-t bg-gray-50/50 p-4">
          {isEditing ? (
            <ExampleEditForm
              example={example}
              onCancel={handleEditCancel}
              onSuccess={handleEditSuccess}
            />
          ) : (
            // View mode
            <div className="space-y-4">
              {/* Answer */}
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Antwort:</Label>
                <p className="text-sm mt-1 whitespace-pre-wrap">{example.answer}</p>
              </div>

              {/* Bad example explanation */}
              {!example.is_good_example && example.explanation && (
                <div>
                  <Label className="text-sm font-medium text-red-600">Warum ist das schlecht:</Label>
                  <p className="text-sm text-red-600 mt-1">{example.explanation}</p>
                </div>
              )}

              {/* Metadata */}
              {(example.submachine_part || example.tags.length > 0) && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Details:</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {example.submachine_part && (
                      <Badge variant="outline">{example.submachine_part}</Badge>
                    )}
                    {example.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Eye className="h-3 w-3" />
                    <span>Verwendet: {example.usage_count}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <User className="h-3 w-3" />
                  <span>Von: {example.created_by}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
